import React, {createContext, ReactNode, useContext, useState, useCallback, useMemo} from 'react'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext'
import {useUIGameVoteContext} from '../UIGameVote/UIGameVoteContext'
import {VoteTypes} from '@colonist/game-base-types'

interface UIGamePauseContextProps {
    isPaused: boolean
    remainingPauseTimeMS: number | undefined
    requestPause: () => void
    requestResume: () => void
}

const UIGamePauseContext = createContext<UIGamePauseContextProps | undefined>(undefined)

export const useUIGamePauseContext = () => {
    const context = useContext(UIGamePauseContext)
    if(!context) throw new Error('useUIGamePauseContext must be used within the UIGamePauseProvider')
    return context
}

let globalIsPaused: undefined | ((isPaused: boolean) => void)
export function setGlobalIsPaused(isPaused: boolean) {
    if(globalIsPaused) globalIsPaused(isPaused)
}

let globalRemainingPauseTimeMS: undefined | ((remainingPauseTimeMS: number | undefined) => void)
export function setGlobalRemainingPauseTimeMS(remainingPauseTimeMS: number | undefined) {
    if(globalRemainingPauseTimeMS) globalRemainingPauseTimeMS(remainingPauseTimeMS)
}

interface UIGamePauseProviderProps {
    children: ReactNode
    isReplay: boolean
}

export const UIGamePauseProvider = ({
    children,
    isReplay,
}: UIGamePauseProviderProps) => {
    const [isPaused, setIsPaused] = useState(false)
    const [remainingPauseTimeMS, setRemainingPauseTimeMS] = useState<number | undefined>(undefined)
    const {sendTogglePause} = useUIGameSocketContext()
    const {isMyVotePending, onAccept, voteState} = useUIGameVoteContext()

    globalIsPaused = setIsPaused
    globalRemainingPauseTimeMS = setRemainingPauseTimeMS

    const requestPause = useCallback(() => {
        if(isPaused) return
        if(isReplay) return

        sendTogglePause()
    }, [isPaused, isReplay, sendTogglePause])

    const requestResume = useCallback(() => {
        if(!isPaused) return
        if(isReplay) return

        if(isMyVotePending && voteState?.type === VoteTypes.TogglePause) {
            onAccept()
        } else {
            sendTogglePause()
        }
    }, [isPaused, isReplay, sendTogglePause, isMyVotePending, onAccept])

    const contextValue = useMemo(() => ({
        isPaused: isPaused,
        remainingPauseTimeMS: remainingPauseTimeMS,
        requestPause: requestPause,
        requestResume: requestResume,
    }), [isPaused, remainingPauseTimeMS, requestPause, requestResume])

    return <UIGamePauseContext.Provider value={contextValue}>
        {children}
    </UIGamePauseContext.Provider>
}
